// #357 — first-party funnel events for the Credits & Usage page + Add Credits modal.
// Pure builders (unit-tested) + a fire-and-forget hook. The backend action
// `record-credits-event` (credits-event-actions.ts) allowlists the names and the prop keys;
// keep the two in sync. Telemetry must NEVER break the page, so every failure is swallowed.
import { useCallback } from 'react';
import { useRecordCreditsEventMutation } from '@common-reducers/backend-api/backend-api';
import { planCycleFromPlanId } from './unlimited-credits-addon';

export type CreditsEventName =
  | 'credits_page_view'
  | 'add_credits_open'
  | 'pack_select'
  | 'addon_select'
  | 'checkout_start';

export type CreditsUsageState = 'healthy' | '50' | '80' | '100';
export type AddCreditsSource = 'header' | 'manage_subscription_link' | 'warning_alert' | 'url_param' | 'unknown';
export type CreditsAddon = 'auto_replies' | 'unlimited_credits';

export type CreditsEventProps = Record<string, string | number | boolean>;

interface CreditsDataLike {
  totalCredits?: number;
  creditsRemaining?: number;
  percentageUsed?: number;
  daysRemaining?: number;
}

/** Usage band the page is in: 100 = out of credits (or fully used), 80 / 50 = the nudge thresholds. */
export function creditsUsageState(pctUsed: number, creditsRemaining: number): CreditsUsageState {
  if (creditsRemaining <= 0 || pctUsed >= 100) return '100';
  if (pctUsed >= 80) return '80';
  if (pctUsed >= 50) return '50';
  return 'healthy';
}

/** 'Pro-USD-Monthly' -> 'Pro'; 'StandardNew-USD-Yearly' -> 'Standard'; none -> 'Free'. */
export function planTier(planId?: string | null): string {
  const family = (planId ?? '').split('-')[0];
  if (!family) return 'Free';
  return family === 'StandardNew' ? 'Standard' : family;
}

const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

/** Props stamped on every credits event: who is looking, on what plan, how close to the wall. */
export function buildCreditsContext(args: {
  isOwner: boolean;
  planId?: string | null;
  creditsData?: CreditsDataLike | null;
}): CreditsEventProps {
  const { isOwner, planId, creditsData } = args;
  const out: CreditsEventProps = { role: isOwner ? 'owner' : 'member', tier: planTier(planId) };
  if (planId) out.plan = planId;
  const cycle = planCycleFromPlanId(planId);
  if (cycle) out.billingCycle = cycle;
  const remaining = num(creditsData?.creditsRemaining);
  const total = num(creditsData?.totalCredits);
  const pct = num(creditsData?.percentageUsed);
  if (remaining !== undefined) out.creditsRemaining = Math.round(remaining);
  if (total !== undefined) out.totalCredits = Math.round(total);
  if (pct !== undefined) out.pctUsed = Math.round(pct * 10) / 10;
  const days = num(creditsData?.daysRemaining);
  if (days !== undefined) out.daysToRenewal = days;
  if (pct !== undefined && remaining !== undefined) out.state = creditsUsageState(pct, remaining);
  return out;
}

/** Props for a credit-pack event (select / checkout). */
export function buildPackProps(pkg: { credits: number }, pricing: { id: string; price: number }, billingCycle: string): CreditsEventProps {
  return { packId: pricing.id, credits: pkg.credits, price: pricing.price, billingCycle };
}

/** Returns `track(name, props)`: fire-and-forget, never throws, never blocks the UI. */
export function useCreditsEvents() {
  const [record] = useRecordCreditsEventMutation();
  return useCallback(
    (name: CreditsEventName, props: CreditsEventProps = {}) => {
      try {
        Promise.resolve(record({ name, props })).catch(() => {});
      } catch {
        /* telemetry only */
      }
    },
    [record],
  );
}
